import {
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { H5, Small } from 'components/Typography';
import { Edit, Search } from 'lucide-react';
import MenuPanel from 'page-sections/crm/MenuPanel';
import PostListSection from 'page-sections/crm/PostListSection';
import { useState } from 'react';
import { customShadows } from 'theme/shadows';

export default function CommentList() {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isLaptop = useMediaQuery(theme.breakpoints.down('lg'));

  const [leftPanelWidth] = useState(1.5);
  const [boardFilter, setBoardFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('week');
  const [keywordSearch, setKeywordSearch] = useState('');

  const customStyle = {
    width: '100%',
    overflow: 'hidden',
    backgroundColor: '#fafafa',
  };

  const dropdownMenuProps = {
    disableScrollLock: true,
    PaperProps: {
      sx: {
        mt: 1,
        borderRadius: '20px',
        boxShadow: customShadows.card,
        minWidth: 220,
        '& .MuiMenuItem-root': {
          fontSize: '14px',
          paddingY: 1,
          paddingX: 2.5,
          color: '#4B4F5C',
        },
        '& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover':
          {
            color: theme.palette.primary.mainBlue,
            backgroundColor: 'transparent',
            fontWeight: 700,
          },
      },
    },
  };

  return (
    <Box sx={customStyle}>
      <Grid container alignItems={'flex-start'}>
        <Grid
          item
          xs={12}
          container
          direction="row"
          alignItems="flex-start"
          bgcolor={'#ffffff'}
          height={'100%'}
          borderRight={`1px solid #E1E1E1`}
        >
          {/* Left Panel */}
          <Grid
            item
            lg={leftPanelWidth}
            xs={12}
            order={isLaptop ? 2 : 0}
            display={'flex'}
            flexDirection={'row'}
            sx={{ overflow: 'hidden' }}
            height={'100%'}
          >
            <MenuPanel />
          </Grid>

          {/* Middle Section */}
          <Grid
            container
            item
            lg={12 - leftPanelWidth}
            xs={12}
            bgcolor={'#fafafa'}
            borderLeft={`1px solid #E1E1E1`}
          >
            <Grid item lg={12} xs={12}>
              <Box
                pt={3}
                pb={3}
                sx={{
                  borderBottom: `1px solid #E1E1E1`,
                  overflow: 'auto',
                }}
              >
                <Box
                  px={isTablet ? 2 : 4}
                  display="flex"
                  flexDirection="column"
                  gap={3}
                >
                  {/* Search Filters */}
                  <Card
                    sx={{
                      padding: '18px 20px',
                      borderRadius: '24px',
                      boxShadow: customShadows.card,
                      backgroundColor: '#ffffff',
                      border: 'none',
                    }}
                  >
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Grid item xs={12} md={8.5}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={3}>
                            <Small
                              sx={{
                                color: '#5D6473',
                                mb: 1,
                                display: 'block',
                              }}
                            >
                              게시판
                            </Small>
                            <FormControl fullWidth size="small">
                              <Select
                                value={boardFilter}
                                onChange={e => setBoardFilter(e.target.value)}
                                displayEmpty
                                MenuProps={dropdownMenuProps}
                                sx={{
                                  backgroundColor: '#F5F5F7',
                                  borderRadius: '999px',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                  },
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                    {
                                      border: 'none',
                                    },
                                  fontSize: '14px',
                                  px: 2,
                                }}
                              >
                                <MenuItem value="all">전체 게시판</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Small
                              sx={{
                                color: '#5D6473',
                                mb: 1,
                                display: 'block',
                              }}
                            >
                              유형
                            </Small>
                            <FormControl fullWidth size="small">
                              <Select
                                value={typeFilter}
                                onChange={e => setTypeFilter(e.target.value)}
                                displayEmpty
                                MenuProps={dropdownMenuProps}
                                sx={{
                                  backgroundColor: '#F5F5F7',
                                  borderRadius: '999px',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                  },
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                    {
                                      border: 'none',
                                    },
                                  fontSize: '14px',
                                  px: 2,
                                }}
                              >
                                <MenuItem value="all">
                                  전체 (게시글+댓글)
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Small
                              sx={{
                                color: '#5D6473',
                                mb: 1,
                                display: 'block',
                              }}
                            >
                              기간
                            </Small>
                            <FormControl fullWidth size="small">
                              <Select
                                value={periodFilter}
                                onChange={e => setPeriodFilter(e.target.value)}
                                displayEmpty
                                MenuProps={dropdownMenuProps}
                                sx={{
                                  backgroundColor: '#F5F5F7',
                                  borderRadius: '999px',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                  },
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                    {
                                      border: 'none',
                                    },
                                  fontSize: '14px',
                                  px: 2,
                                }}
                              >
                                <MenuItem value="week">최근 7일</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Small
                              sx={{
                                color: '#5D6473',
                                mb: 1,
                                display: 'block',
                              }}
                            >
                              키워드
                            </Small>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="작성자/키워드 검색"
                              value={keywordSearch}
                              onChange={e => setKeywordSearch(e.target.value)}
                              InputProps={{
                                sx: {
                                  backgroundColor: '#F5F5F7',
                                  borderRadius: '999px',
                                  '& fieldset': { border: 'none' },
                                  fontSize: '14px',
                                  px: 2,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={3.5}
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: 1,
                          mt: { xs: 1.5, md: 0 },
                        }}
                      >
                        <Button
                          variant="contained"
                          startIcon={<Search size={18} />}
                          sx={{
                            backgroundColor: theme.palette.primary.mainBlue,
                            color: '#ffffff',
                            borderRadius: '12px',
                            padding: '8px 20px',
                            fontSize: '14px',
                            fontWeight: 600,
                            textTransform: 'none',
                            boxShadow: 'none',
                            '& .MuiButton-startIcon': {
                              marginRight: 1,
                            },
                            '&:hover': {
                              backgroundColor:
                                theme.palette.primary.mainBlueHover,
                              boxShadow: 'none',
                            },
                          }}
                        >
                          검색
                        </Button>
                        {/* Removed Reset Button as it wasn't in the main requirement, but kept Search */}
                      </Grid>
                    </Grid>
                  </Card>

                  {/* List Area */}
                  <Card
                    sx={{
                      padding: '18px 20px 8px',
                      borderRadius: '18px',
                      boxShadow: customShadows.card,
                      backgroundColor: '#ffffff',
                      border: 'none',
                    }}
                  >
                    <Box mb={2}>
                      <H5
                        sx={{
                          fontWeight: 600,
                          color: '#151515',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Edit
                          size={24}
                          color={theme.palette.primary.mainBlue}
                        />{' '}
                        게시글 & 댓글 목록
                      </H5>
                    </Box>

                    <Box>
                      <PostListSection
                        isTablet={isTablet}
                        isMobile={isTablet}
                      />
                    </Box>
                  </Card>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
